import React, { useEffect, useMemo, useCallback, useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Box,
  Autocomplete,
  CircularProgress,
  Alert,
  FormHelperText,
} from "@mui/material";
import { Send, ArrowForward } from "@mui/icons-material";
import { useForm, Controller } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { endpoints } from "../../../utils/endpoints";
import { getBatteryIds } from "../../../hooks/use-battery";
import { getStateNames } from "../../../hooks/use-state";
import { useAuth } from "../../../contexts/AuthContext";

const BATTERY_STATUS_OPTIONS = [
  "in-transit",
  "received",
  "rejected",
  "cancelled",
];

const movementSchema = yup.object({
  batteryId: yup.string().required("Battery ID is required"),
  status: yup.string(),
  fromState: yup.string().required("From state is required"),
  toState: yup
    .string()
    .required("To state is required")
    .test(
      "different-states",
      "To and from states must be different",
      function (value) {
        return value !== this.parent.fromState;
      },
    ),
  remark: yup.string().max(500, "Remarks must be less than 500 characters"),
});

const DEFAULT_VALUES = {
  batteryId: "",
  fromState: "",
  toState: "",
  remark: "",
  status: "",
};

const InitiateMovementForm = ({
  refetch,
  mode = "create",
  initialData = null,
  onClose,
  onSuccess,
  sx,
  contentSx,
}) => {
  const { user } = useAuth();
  const [submitError, setSubmitError] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const isEditMode = mode === "edit";
  const userEmail = user?.email || "Unknown User";

  const form = useForm({
    resolver: yupResolver(movementSchema),
    defaultValues: DEFAULT_VALUES,
  });

  const {
    handleSubmit,
    control,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = form;

  const queries = {
    batteries: useQuery({
      queryKey: ["batteryIds"],
      queryFn: () => getBatteryIds(""),
      staleTime: 5 * 60 * 1000,
      retry: 2,
    }),
    states: useQuery({
      queryKey: ["stateNames"],
      queryFn: getStateNames,
      staleTime: 10 * 60 * 1000,
      retry: 2,
    }),
  };

  const batteryOptions = useMemo(() => {
    const data = queries.batteries.data;
    if (!data || !Array.isArray(data)) return [];

    return data.map((id) => ({ label: id, value: id }));
  }, [queries.batteries.data]);

  const fromState = watch("fromState");
  const availableToStates = useMemo(
    () =>
      queries.states?.data?.filter((loc) => loc.stateName !== fromState) || [],
    [queries.states.data, fromState],
  );

  const initialFormValues = useMemo(() => {
    if (!isEditMode || !initialData) return DEFAULT_VALUES;
    return {
      batteryId: initialData.batteryId || "",
      fromState: initialData.state?.from?.value || "",
      toState: initialData.state?.to?.value || "",
      remark: initialData.remark || "",
      status: initialData.status?.value || "",
    };
  }, [isEditMode, initialData]);

  useEffect(() => {
    if (isEditMode && initialData) {
      reset(initialFormValues);
    }
  }, [isEditMode, initialData, reset, initialFormValues]);

  const getChangedFields = useCallback(
    (formData) => {
      if (!isEditMode || !initialData) return null;

      const changedFields = {};
      let hasChanges = false;

      const fieldsToCheck = ["fromState", "toState", "remark", "status"];

      fieldsToCheck.forEach((field) => {
        const initialValue = initialFormValues[field] || "";
        const currentValue = formData[field] || "";

        if (initialValue !== currentValue) {
          changedFields[field] = currentValue;
          hasChanges = true;
        }
      });

      return hasChanges ? changedFields : null;
    },
    [isEditMode, initialData, initialFormValues],
  );

  const stateTypeMap = useMemo(() => {
    if (!queries.states.data) return {};
    return queries.states.data.reduce((acc, curr) => {
      acc[curr.stateName] = curr.stateType;
      return acc;
    }, {});
  }, [queries.states.data]);

  const getStateType = (stateName) => stateTypeMap[stateName] || null;

  const createPayload = useCallback(
    (data, changedFields) => {
      const basePayload = {
        batteryId: data.batteryId,
        changedBy: userEmail,
      };

      const resolveState = (stateValue) => ({
        value: stateValue,
        type: getStateType(stateValue),
      });

      if (!isEditMode) {
        return {
          ...basePayload,
          source: "manual",
          manual: true,
          initiatedBy: userEmail,
          status: { value: "in-transit", changedBy: userEmail },
          state: {
            from: resolveState(data.fromState),
            to: resolveState(data.toState),
          },
          ...(data.remark && { remark: data.remark }),
        };
      }

      const payload = { ...basePayload };

      if (changedFields.status) {
        payload.status = {
          value: changedFields.status,
          changedBy: userEmail,
        };
      }

      const fromState =
        changedFields.fromState ?? initialData.state?.from?.value;
      const toState = changedFields.toState ?? initialData.state?.to?.value;

      if (changedFields.fromState || changedFields.toState) {
        payload.state = {
          from: resolveState(fromState),
          to: resolveState(toState),
        };
      }

      if ("remark" in changedFields) {
        payload.remark = changedFields.remark;
      }

      return payload;
    },
    [isEditMode, userEmail, initialData, getStateType],
  );

  const onSubmit = useCallback(
    async (data) => {
      setSubmitSuccess(false);
      setSubmitError("");

      try {
        let payload;

        if (isEditMode) {
          const changedFields = getChangedFields(data);
          if (!changedFields) {
            setSubmitError(
              "No changes detected. Please modify the data before saving.",
            );
            return;
          }
          payload = createPayload(data, changedFields);
        } else {
          payload = createPayload(data);
        }

        const endpoint = isEditMode
          ? `${
              endpoints.battery.updateBatteryMovement
            }/${initialData.id.trim()}`
          : endpoints.battery.initiateBatteryMovementV2;

        if (isEditMode) {
          await axios.patch(endpoint, payload);
        } else {
          await axios.post(endpoint, payload);
        }

        setSubmitSuccess(true);
        reset();
        refetch?.();
        onSuccess?.();
        onClose?.();
      } catch (err) {
        const msg =
          err?.response?.data?.message ||
          err.message ||
          "Something went wrong.";
        setSubmitError(msg);
      }
    },
    [
      isEditMode,
      getChangedFields,
      createPayload,
      reset,
      refetch,
      onSuccess,
      onClose,
    ],
  );

  const clearAlerts = useCallback(() => {
    setSubmitSuccess(false);
    setSubmitError("");
  }, []);

  const renderAlerts = () => (
    <>
      {submitSuccess && (
        <Alert severity="success" onClose={clearAlerts}>
          Battery movement {isEditMode ? "updated" : "initiated"} successfully!
        </Alert>
      )}
      {submitError && (
        <Alert severity="error" onClose={clearAlerts}>
          {submitError}
        </Alert>
      )}
      {queries.states.error && (
        <Alert severity="error">{queries.states.error}</Alert>
      )}
      {queries.batteries.error && (
        <Alert severity="error">{queries.batteries.error}</Alert>
      )}
    </>
  );

  const renderBatteryField = () => (
    <Controller
      name="batteryId"
      control={control}
      render={({ field }) => (
        <Autocomplete
          {...field}
          options={batteryOptions}
          loading={queries.batteries.isLoading}
          disabled={isEditMode}
          getOptionLabel={(option) =>
            typeof option === "string" ? option : option?.label || ""
          }
          isOptionEqualToValue={(opt, val) => opt.value === (val?.value || val)}
          onChange={(_, value) => field.onChange(value?.value || "")}
          value={
            (batteryOptions ?? []).find((opt) => opt.value === field.value) ||
            null
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select Battery ID"
              error={!!errors.batteryId}
              helperText={errors.batteryId?.message}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {queries.batteries.isLoading && (
                      <CircularProgress color="inherit" size={20} />
                    )}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      )}
    />
  );

  const renderStateFields = () => (
    <Box
      display="flex"
      alignItems="center"
      gap={2}
      flexDirection={{ xs: "column", md: "row" }}
    >
      <Controller
        name="fromState"
        control={control}
        render={({ field }) => (
          <Autocomplete
            {...field}
            fullWidth
            options={queries.states.data || []}
            getOptionLabel={(option) => option?.stateName || ""}
            isOptionEqualToValue={(option, value) =>
              option.stateName === (value?.stateName || value)
            }
            onChange={(_, value) => field.onChange(value?.stateName || "")}
            value={
              (queries.states.data || []).find(
                (opt) => opt.stateName === field.value,
              ) || null
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="From State"
                error={!!errors.fromState}
                helperText={errors.fromState?.message}
              />
            )}
          />
        )}
      />

      <ArrowForward sx={{ fontSize: 28, color: "text.secondary" }} />

      <Controller
        name="toState"
        control={control}
        render={({ field }) => (
          <Autocomplete
            {...field}
            fullWidth
            options={availableToStates}
            getOptionLabel={(option) => option?.stateName || ""}
            getOptionDisabled={(option) => option?.stateName === fromState}
            isOptionEqualToValue={(option, value) =>
              option.stateName === (value?.stateName || value)
            }
            onChange={(_, value) => field.onChange(value?.stateName || "")}
            value={
              availableToStates.find((opt) => opt.stateName === field.value) ||
              null
            }
            disabled={!fromState}
            renderInput={(params) => (
              <TextField
                {...params}
                label="To State"
                error={!!errors.toState}
                helperText={errors.toState?.message}
              />
            )}
          />
        )}
      />
    </Box>
  );

  const renderStatusField = () =>
    isEditMode && (
      <Controller
        name="status"
        control={control}
        render={({ field }) => (
          <FormControl fullWidth error={!!errors.status}>
            <InputLabel>Status</InputLabel>
            <Select {...field} label="Status">
              {BATTERY_STATUS_OPTIONS.map((status) => (
                <MenuItem key={status} value={status}>
                  {status}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>{errors.status?.message}</FormHelperText>
          </FormControl>
        )}
      />
    );

  const renderRemarkField = () => (
    <Controller
      name="remark"
      control={control}
      render={({ field }) => (
        <TextField
          {...field}
          label="Remarks (Optional)"
          multiline
          rows={4}
          placeholder="Add any additional information here..."
          error={!!errors.remark}
          helperText={errors.remark?.message}
          inputProps={{ maxLength: 500 }}
        />
      )}
    />
  );

  return (
    <Card sx={{ mx: "auto", mt: 2, borderRadius: 3, boxShadow: 3, ...sx }}>
      <CardContent sx={{ p: 4, ...contentSx }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            mb: 3,
            display: "flex",
            alignItems: "center",
          }}
        >
          <Send sx={{ mr: 1, color: "primary.main" }} />
          {isEditMode ? "Edit Battery Movement" : "Initiate Battery Movement"}
        </Typography>

        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <Stack spacing={3}>
            {renderAlerts()}
            {renderBatteryField()}
            {renderStateFields()}
            {renderStatusField()}
            {renderRemarkField()}

            <Button
              fullWidth
              type="submit"
              variant="contained"
              color="primary"
              disabled={isSubmitting || queries.batteries.isLoading}
              startIcon={
                isSubmitting ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <Send />
                )
              }
              sx={{
                fontWeight: 600,
                borderRadius: 2,
                textTransform: "none",
                py: 1.5,
              }}
            >
              {isSubmitting
                ? isEditMode
                  ? "Saving..."
                  : "Submitting..."
                : isEditMode
                  ? "Save Changes"
                  : "Initiate Movement"}
            </Button>
          </Stack>
        </form>
      </CardContent>
    </Card>
  );
};

export default React.memo(InitiateMovementForm);
